import { State } from './types.js'
import {
    createApi,
    Api,
} from './api.js'
import { getRootProps } from './props/root.js'
import { Root } from './root.js'
import React from 'react'
import {
    Root as ReactRoot,
    createRoot,
} from 'react-dom/client'
import websocketService, { WebSocketMessageType } from './services/websocket.js'
import {
    Room,
    RoomModel,
} from '../common/models/RoomModel.js'

export class View {
    state: State
    searchQuery?: URLSearchParams
    api: Api
    root: ReactRoot
    private updateInterval?: number

    constructor(container: Element) {
        this.state = {
            rooms: [],
            isLoading: true,
            latestError: undefined,
        }

        this.searchQuery = this.getAndCleanSearchQuery()
        this.root = createRoot(container)
        this.api = createApi(this)
        this.setupWebsocket()
        this.setupAutoUpdate()

        // Check if there's a room ID in the URL hash
        this.checkUrlForRoomId()

        // load initial data
        void this.api.fetchRooms()
    }

    public checkUrlForRoomId() {
        const hash = window.location.hash
        if (hash && hash.startsWith('#room=')) {
            const roomId = hash.substring(6) // Remove '#room=' prefix
            this.state.selectedRoomId = roomId
        } else if (hash === '' || hash === '#') {
            // Clear the selected room if the hash is empty or just '#'
            this.state.selectedRoomId = undefined
        }
    }

    private setupAutoUpdate() {
        this.updateInterval = window.setInterval(() => {
            this.update()
        }, 10 * 1000)
    }

    private setupWebsocket() {
        websocketService.connect()
        websocketService.addMessageListener(WebSocketMessageType.ROOMS_UPDATE, (data: Room[]) => this.updateRooms(data))
    }

    public setSelectedRoom(roomId?: string) {
        this.state.selectedRoomId = roomId

        // Update the URL hash
        if (roomId) {
            window.history.pushState(null, '', `#room=${roomId}`)
        } else {
            window.history.pushState(null, '', '#')
        }

        this.update()
    }

    private getAndCleanSearchQuery() {
        if (window.location.search) {
            const query = window.location.search
            const newUrl = `${window.location.origin}/${window.location.hash}`
            window.history.pushState({ path: newUrl }, '', newUrl)

            return new URLSearchParams(query)
        }

        return undefined
    }

    private updateRooms(rooms: Room[]) {
        this.state.rooms = rooms.map((room) => new RoomModel(room))
        this.update()
    }

    public update() {
        const props = getRootProps(this)
        this.root.render(<Root {...props} />)
    }

    public startLoading() {
        this.state.isLoading = true
        this.state.latestError = undefined
        this.update()
    }

    public endLoading() {
        this.state.isLoading = false
        this.update()
    }

    public destroy() {
        if (this.updateInterval) {
            window.clearInterval(this.updateInterval)
            this.updateInterval = undefined
        }
    }
}
